import { memo } from 'react'
import { useAccessToken } from '@chilecompra/react-kit'
import { Card, Toast, Typography } from '@chilecompra/react-kit/components'
import PropTypes from 'prop-types'

import { EXPIRES_AT, USER_TYPES_TRASLATE_DICTIONARY } from '../../configs/settings/constants'
import { REACT_APP_PROVIDERS_BASE_URL } from '../../configs/settings/environment'
import AddResponseForm from './RequestDetailInfo.addResponse'
import {
  ArrowForward,
  AttachedFilesLink,
  AttachedFilesResponseWrapper,
  Container,
  Item,
  Strong,
  SummaryLink,
  WrapperQuestions,
  WrapperResponse,
  WrapperResponses
} from './RequestDetailInfo.styles'

/**
 * The RequestDetailInfo's questionsList.
 */
const QuestionsList = props => {
  const {
    countries,
    detailUsrCode,
    errorCountries,
    formType,
    isClose,
    isWillBePublic,
    loadingCountries,
    onAddFile,
    onDownloadFile,
    onRemoveFile,
    onResponse,
    onSignedUniquePass,
    publicUserDni,
    publicUserName,
    questions,
    usrCode
  } = props || {}

  const accessToken = useAccessToken()

  const handleGoToSellerCard = dni => () => {
    window.location.href = `${REACT_APP_PROVIDERS_BASE_URL}/ficha/callback?access_token=${accessToken}&expires_in=${EXPIRES_AT}&rut=${dni}`
  }

  return questions.map(question => {
    const responseUser =
      (question.multipleChoiceOptions?.length !== 0 &&
        question.responses?.find(response => response.usrCode?.id === usrCode)) ||
      undefined

    return (
      <Item key={question.id} margin="0 0 32px" xs={12}>
        <Card color="blank" enableBorder padding="24px">
          <WrapperQuestions margin="0 0 32px">
            <Item>
              <Typography fontWeight="bold" margin="0 0 4px" variant="body2">
                {question.usrCode?.name}
              </Typography>

              <Typography margin="0 0 4px" variant="subtitle">
                {question.entCode?.name}
              </Typography>

              <Typography variant="subtitle">{question.orgCode?.name}</Typography>
            </Item>

            <Item>
              <Typography
                className="question"
                margin={question.attachedFiles.length > 0 ? '0 0 8px' : undefined}
                variant="body2"
              >
                {question.message}
              </Typography>

              {question.attachedFiles.length > 0 &&
                question.attachedFiles.map(file => (
                  <AttachedFilesResponseWrapper key={file.id}>
                    <AttachedFilesLink onClick={() => onDownloadFile(file)}>{file.name}</AttachedFilesLink>
                  </AttachedFilesResponseWrapper>
                ))}
            </Item>
          </WrapperQuestions>

          {question.responses.map(response => {
            const isBuyer = response.entCode?.userType === USER_TYPES_TRASLATE_DICTIONARY.BUYER
            const isSeller = response.entCode?.userType === USER_TYPES_TRASLATE_DICTIONARY.SELLER

            return (
              <WrapperResponses key={response.id} margin="0 0 32px">
                <Item>
                  <Typography fontWeight="bold" margin="0 0 4px" variant="body2">
                    {response.usrCode?.name || response.publicUserUniquePass?.name || response.publicUserForeign?.name}
                  </Typography>

                  {isBuyer && response.entCode?.name && (
                    <Typography margin="0 0 4px" variant="subtitle">
                      {response.entCode?.name}
                    </Typography>
                  )}

                  {isSeller && response.entCode?.name && (
                    <Container margin="0 0 4px">
                      <SummaryLink onClick={handleGoToSellerCard(response.entCode?.dni)} target="_blank">
                        {response.entCode?.name}
                      </SummaryLink>
                    </Container>
                  )}

                  {response.orgCode?.name && <Typography variant="subtitle">{response.orgCode?.name}</Typography>}
                </Item>

                {isWillBePublic && (
                  <WrapperResponse>
                    <ArrowForward />

                    <Typography className="response" color="black3" variant="body2">
                      {response.message}
                    </Typography>

                    {response.attachedFiles.length > 0 &&
                      response.attachedFiles.map(file => (
                        <AttachedFilesResponseWrapper key={file.id}>
                          <AttachedFilesLink onClick={() => onDownloadFile(file)}>{file.name}</AttachedFilesLink>
                        </AttachedFilesResponseWrapper>
                      ))}
                  </WrapperResponse>
                )}

                {!isWillBePublic && (
                  <>
                    {detailUsrCode.id === usrCode && (
                      <WrapperResponse>
                        <ArrowForward />

                        <Typography className="response" color="black3" variant="body2">
                          {response.message}
                        </Typography>

                        {response.attachedFiles.length > 0 &&
                          response.attachedFiles.map(file => (
                            <AttachedFilesResponseWrapper key={file.id}>
                              <AttachedFilesLink onClick={() => onDownloadFile(file)}>{file.name}</AttachedFilesLink>
                            </AttachedFilesResponseWrapper>
                          ))}
                      </WrapperResponse>
                    )}

                    {detailUsrCode.id !== usrCode && (
                      <Toast severity="info">
                        Las respuestas de esta Consulta al Mercado son de caracter <Strong>privado</Strong>, y solamente
                        el organismo comprador puede visualizarlas.
                      </Toast>
                    )}
                  </>
                )}
              </WrapperResponses>
            )
          })}

          {!isClose && (
            <AddResponseForm
              countries={countries}
              errorCountries={errorCountries}
              formType={formType}
              isError={question.error}
              isLoading={question.loading}
              isResponseMultipleOptions={responseUser?.usrCode?.id === usrCode}
              loadingCountries={loadingCountries}
              multipleChoiceOptions={question.multipleChoiceOptions}
              onAdd={onAddFile}
              onRemove={onRemoveFile}
              onResponse={onResponse}
              onSignedUniquePass={onSignedUniquePass}
              publicUserDni={publicUserDni}
              publicUserName={publicUserName}
              questionId={question.id}
              responseUser={responseUser}
              tempResponseAttachedFiles={question.tempResponseAttachedFiles}
            />
          )}
        </Card>
      </Item>
    )
  })
}

QuestionsList.defaultProps = {
  countries: [],
  errorCountries: null,
  loadingCountries: false
}

QuestionsList.propTypes = {
  countries: PropTypes.array,
  detailUsrCode: PropTypes.shape().isRequired,
  errorCountries: PropTypes.any,
  formType: PropTypes.string.isRequired,
  isClose: PropTypes.bool.isRequired,
  isWillBePublic: PropTypes.bool.isRequired,
  loadingCountries: PropTypes.bool,
  onAddFile: PropTypes.func.isRequired,
  onDownloadFile: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  onResponse: PropTypes.func.isRequired,
  onSignedUniquePass: PropTypes.func.isRequired,
  publicUserDni: PropTypes.string.isRequired,
  publicUserName: PropTypes.string.isRequired,
  questions: PropTypes.array.isRequired,
  usrCode: PropTypes.string.isRequired
}

export default memo(QuestionsList)
